<template>
  <div>
    <DynamicModal
      :showModal="showModalProp"
      :persistent="false"
      @clickOutside="closeModal"
      title="One Off Fees"
      maxWidth="900px"
    >
      <v-container fluid slot="title-buttons" class="py-1">
        <v-row justify="end">
          <v-btn
            icon
            depressed
            color="primary"
            @click="closeModal"
          >
            <v-icon color="white">close</v-icon>
          </v-btn>
        </v-row>
      </v-container>
      <v-container fluid slot="content">
        <v-row class="mt-2">
          <v-col cols="12">
            <SimplePageHeader title="Assignment"></SimplePageHeader>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              label="Consultant"
              v-bind:value="userName"
              readonly
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              label="Client"
              v-bind:value="clientName"
              readonly
              outlined
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row
          v-if="showDateRangePicker"
          class="my-0"
        >
          <v-col cols="12">
            <DateRange
              class="my-0"
              :showLabel="true"
              :dateBegin="filterDateBeginValue"
              :dateEnd="filterDateEndValue"
              @dateBeginChange="(newValue) => this.filterDateBeginValue = newValue"
              @dateEndChange="(newValue) => this.filterDateEndValue = newValue"
            ></DateRange>
          </v-col>
        </v-row>
        <v-row justify="end" v-if="showDateRangePicker" class="my-0 py-0">
          <v-col cols="12" md="6">
            <FilterButtons
              @search="handleSearchClick"
              @clear="handleClearClick"
            ></FilterButtons>
          </v-col>
        </v-row>
        <FilterButtonsSmall
          v-if="showDateRangePicker"
          @search="handleSearchClick"
          @clear="handleClearClick"
        ></FilterButtonsSmall>
        <v-row class="mt-3">
          <v-col cols="12">
            <SimplePageHeader title="One Off Fees Records">
              <template v-slot:right-buttons>
                <v-btn
                  v-if="!readonly"
                  block
                  outlined
                  color="primary"
                  @click="showOneOffFeeAddModal"
                >
                  Add Fee
                  <v-icon right >add</v-icon>
                </v-btn>
              </template>
            </SimplePageHeader>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-layout column>
              <v-data-table
                dense
                light
                :headers="headers"
                :items="oneOffFeesRecordsList"
                :items-per-page=5
              >
                <template v-slot:item.payAmount="{ item }">
                  {{ item.payAmount | currency }}
                </template>
                <template v-slot:item.billAmount="{ item }">
                  {{ item.billAmount | currency }}
                </template>
                <template v-slot:item.date="{ item }">
                  {{ item.date | formatDate }}
                </template>
                <template v-slot:item.action="{ item }">
                  <v-btn
                    v-if="!readonly"
                    class="mx-1 my-1"
                    color="primary"
                    outlined
                    @click="showOneOffFeeEditModal(item)"
                  >
                    <v-icon color="primary">edit</v-icon>
                  </v-btn>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-if="readonly"
                        class="mx-1 my-1"
                        color="primary"
                        outlined
                        v-bind="attrs"
                        v-on="on"
                        @click="showOneOffInfoModal(item)"
                      >
                        <v-icon color="primary">visibility</v-icon>
                      </v-btn>
                    </template>
                    <span>Show One Off Fee Info.</span>
                  </v-tooltip>
                  <v-btn
                    v-if="!readonly"
                    class="mx-1 my-1"
                    color="red"
                    outlined
                    @click="showOneOffFeeDeleteDialog(item)"
                  >
                    <v-icon color="red">delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-layout>
          </v-col>
        </v-row>
      </v-container>
      <v-container fluid slot="buttons" class="pb-0 mb-0">
        <div class="row d-flex justify-end">
          <div class="col-12">
            <v-btn
              block
              depressed
              outlined
              @click="closeModal"
            >
              Close
            </v-btn>
          </div>
        </div>
      </v-container>
    </DynamicModal>
    <OneOffFeeAddModal
      :assignmentId="assignmentId"
      :userName="userName"
      :clientName="clientName"
      ref="OneOffFeeAddModal"
      @closeModal="closeOneOffFeeAddModalHandler"
    ></OneOffFeeAddModal>
    <OneOffFeeEditModal
      :assignmentId="assignmentId"
      :userName="userName"
      :clientName="clientName"
      :id="oneOffFeeRecordIdToEdit"
      :description="descriptionToEdit"
      :payAmount="payAmountToEdit"
      :billAmount="billAmountToEdit"
      :date="dateToEdit"
      :notes="notesToEdit"
      ref="OneOffFeeEditModal"
      @descriptionChange="descriptionToEditChangeHandler"
      @payAmountChange="payAmountToEditChangeHandler"
      @billAmountChange="billAmountToEditChangeHandler"
      @dateChange="dateToEditChangeHandler"
      @notesChange="notesToEditChangeHandler"
      @closeModal="closeOneOffFeeEditModalHandler"
    ></OneOffFeeEditModal>
    <ConfirmationModal
      :title="'Delete Confirmation'"
      :contentText="'Are you sure you want to delete the selected one off fee record?'"
      :toolbarColor="'primary'"
      @cancel-click="closeOneOffFeeDeleteConfirmationDialog"
      @ok-click="confirmOneOffFeeDelete"
      :showModal="showOneOffFeeDeleteConfirmationDialog"
    ></ConfirmationModal>
    <OneOffFeeInfoModal
      ref="OneOffFeeInfoModal"
      :userName="userName"
      :clientName="clientName"
      :oneOffFeeInfo="oneOffFeeInfo"
      @closeModal="closeInfoModalHandler"
    ></OneOffFeeInfoModal>
  </div>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { DynamicModal, ConfirmationModal } from '@/components/dialogs'
import { mapActions } from 'vuex'
import { nextTick } from 'vue'
import { mapFields } from '@/vueHelpers/vuex'
import OneOffFeeAddModal from '@/components/oneOffFeesRecords/OneOffFeeAddModal'
import OneOffFeeEditModal from '@/components/oneOffFeesRecords/OneOffFeeEditModal'
import OneOffFeeInfoModal from '@/components/oneOffFeesRecords/OneOffFeeInfoModal'
import { MonthPicker } from '@/components/shared/datePickers'
import DateRange from '@/components/shared/datePickers/DateRange'
import { FilterButtons, FilterButtonsSmall, ButtonCustomIcon } from '@/components/buttons'

export default {
  components: {
    DateRange,
    OneOffFeeEditModal,
    SimplePageHeader,
    DynamicModal,
    ButtonCustomIcon,
    OneOffFeeAddModal,
    ConfirmationModal,
    OneOffFeeInfoModal,
    MonthPicker,
    FilterButtons,
    FilterButtonsSmall
  },
  props: [
    'assignmentId',
    'userName',
    'clientName',
    'showDateRangePicker',
    'filterDateBegin',
    'filterDateEnd',
    'readonly'
  ],
  data () {
    return {
      showModalProp: false,
      headers: [
        { text: 'Description', value: 'description', width: '30%', sortable: false },
        { text: 'Pay Amount', value: 'payAmount', align: 'center', width: '15%', sortable: false },
        { text: 'Bill Amount', value: 'billAmount', align: 'center', width: '15%', sortable: false },
        { text: 'Date', value: 'date', align: 'center', width: '15%' },
        { text: 'Actions', value: 'action', class: 'text-xs-right', align: 'center', width: '25%', sortable: false }
      ],
      showOneOffFeeDeleteConfirmationDialog: false,
      oneOffFeeRecordIdToEdit: null,
      descriptionToEdit: null,
      payAmountToEdit: null,
      billAmountToEdit: null,
      dateToEdit: null,
      notesToEdit: null,
      oneOffFeeRecordIdToDelete: null,
      oneOffFeeInfo: {},
      tempFilterDateBegin: null,
      tempFilterDateEnd: null
    }
  },
  computed: {
    ...mapFields('oneOffFeesRecords', ['oneOffFeesRecordsList']),
    filterDateBeginValue: {
      get () {
        return this.filterDateBegin ? this.filterDateBegin : this.tempFilterDateBegin
      },
      set (newValue) {
        if (this.filterDateBegin) {
          this.$emit('', newValue)
        } else {
          this.tempFilterDateBegin = newValue
        }
      }
    },
    filterDateEndValue: {
      get () {
        return this.filterDateEnd ? this.filterDateEnd : this.tempFilterDateEnd
      },
      set (newValue) {
        if (this.filterDateEnd) {
          this.$emit('', newValue)
        } else {
          this.tempFilterDateEnd = newValue
        }
      }
    }
  },
  methods: {
    ...mapActions('oneOffFeesRecords', ['getOneOffFeesRecords', 'deleteOneOffFee']),
    async handleSearchClick () {
      await this.showModal()
    },
    async handleClearClick () {
      this.filterDateBeginValue = null
      this.filterDateEndValue = null
      await this.showModal()
    },
    async showModal () {
      await nextTick()
      await this.getOneOffFeesRecords(Object.assign({}, {
        assignmentId: this.assignmentId,
        dateBegin: this.filterDateBeginValue,
        dateEnd: this.filterDateEndValue
      }))
      this.showModalProp = true
    },
    closeModal () {
      this.showModalProp = false
      this.$emit('closeModal')
    },
    showOneOffFeeAddModal () {
      this.closeModal()
      this.$refs.OneOffFeeAddModal.showModal()
    },
    closeOneOffFeeAddModalHandler () {
      this.showModal()
    },
    closeOneOffFeeEditModalHandler () {
      this.showModal()
    },
    showOneOffFeeEditModal (item) {
      this.oneOffFeeRecordIdToEdit = item.id
      this.descriptionToEdit = item.description
      this.payAmountToEdit = item.payAmount
      this.billAmountToEdit = item.billAmount
      this.dateToEdit = item.date
      this.notesToEdit = item.notes
      this.closeModal()
      this.$refs.OneOffFeeEditModal.showModal()
    },
    descriptionToEditChangeHandler (newValue) {
      this.descriptionToEdit = newValue
    },
    payAmountToEditChangeHandler (newValue) {
      this.payAmountToEdit = newValue
    },
    billAmountToEditChangeHandler (newValue) {
      this.billAmountToEdit = newValue
    },
    dateToEditChangeHandler (newValue) {
      this.dateToEdit = newValue
    },
    notesToEditChangeHandler (newValue) {
      this.notesToEdit = newValue
    },
    showOneOffFeeDeleteDialog (item) {
      this.oneOffFeeRecordIdToDelete = item.id
      this.showModalProp = false
      this.showOneOffFeeDeleteConfirmationDialog = true
    },
    async confirmOneOffFeeDelete () {
      await this.deleteOneOffFee(this.oneOffFeeRecordIdToDelete)
      this.showOneOffFeeDeleteConfirmationDialog = false
      await this.showModal()
    },
    closeOneOffFeeDeleteConfirmationDialog () {
      this.showOneOffFeeDeleteConfirmationDialog = false
      this.showModalProp = true
      this.oneOffFeeRecordIdToDelete = null
    },
    showOneOffInfoModal (item) {
      this.oneOffFeeInfo = item
      this.closeModal()
      this.$refs.OneOffFeeInfoModal.showModal()
    },
    closeInfoModalHandler () {
      this.showModal()
    }
  }
}
</script>
